import classnames from 'classnames';
import React__default from 'react';
import { useDropdownState } from './use_dropdown_state.js';
import '../../utils/index.js';
import { Icon } from '../icons/index.js';
import '../list/index.js';
import '../spinner/index.js';
import '../stack/index.js';
import { item, disabled, iconStart, content, spinner, iconEnd } from './dropdown.css.js';
import { useIsMounted } from '../../utils/is_mounted.js';
import { ListItem } from '../list/list_item.js';
import { Stack } from '../stack/stack.js';
import { Spinner } from '../spinner/spinner.js';

/**
 * Renders a single item in a DropdownMenu
 */
const DropdownMenuItem = React__default.forwardRef(function DropdownMenuItem({ className, children, onClick, icon, iconStart: iconStartProp, iconEnd: iconEnd$1, disabled: disabled$1, loading, ...rest }, ref) {
    const { setOpen, refs } = useDropdownState();
    const [waiting, setWaiting] = React__default.useState(false);
    const isMounted = useIsMounted();
    const isLoading = loading || waiting;
    const buttonElement = refs.domReference.current;
    const isDisabled = isLoading || disabled$1;
    // backwards compatibility until `icon` prop exists
    const iconStart$1 = iconStartProp || icon;
    const handleActivation = React__default.useCallback(async (returnFocus) => {
        if (!isDisabled) {
            if (onClick) {
                let promiseResult = undefined;
                setWaiting(true);
                try {
                    promiseResult = await Promise.resolve(onClick());
                }
                finally {
                    if (isMounted()) {
                        setWaiting(false);
                    }
                    if (promiseResult !== false) {
                        setOpen(false);
                        if (returnFocus) {
                            buttonElement?.focus();
                        }
                    }
                }
            }
            else {
                setOpen(false);
                if (returnFocus) {
                    buttonElement?.focus();
                }
            }
        }
    }, [isDisabled, onClick, setOpen, buttonElement, isMounted]);
    const handleClick = React__default.useCallback(async (event) => {
        event.preventDefault();
        event.stopPropagation();
        await handleActivation(false);
    }, [handleActivation]);
    const handleKeyDown = React__default.useCallback(async (event) => {
        switch (event.key) {
            case 'Enter': {
                event.preventDefault();
                event.stopPropagation();
                await handleActivation(true);
                break;
            }
        }
    }, [handleActivation]);
    return (React__default.createElement(ListItem, { ref: ref, role: "menuitem", tabIndex: -1, className: classnames(item, {
            [disabled]: isDisabled,
        }, className), onClick: handleClick, onKeyDown: handleKeyDown, radius: "small", paddingBlock: "xsmall", paddingInline: "small", paddingInlineEnd: "xxlarge", "aria-disabled": isDisabled, ...rest },
        React__default.createElement(Stack, { gap: "small", alignItems: "start", wrap: false },
            React__default.createElement(Icon, { className: iconStart, name: iconStart$1 }),
            React__default.createElement("span", { className: content }, children),
            isLoading ? (React__default.createElement(Spinner, { size: "small", className: spinner })) : (React__default.createElement(Icon, { className: iconEnd, name: iconEnd$1 })))));
});

export { DropdownMenuItem };
